/**
 * Config contract returned by the public embeds endpoint
 * (`GET /public/embeds/:instanceId`) and consumed by each component.
 *
 * Field shapes are derived from the design handoff prototypes
 * (`*_TWEAK_DEFAULTS` blocks + `brands.jsx`). The shared {@link ThemeConfig}
 * mirrors the tokens every prototype reads; each component then has its own
 * options block.
 */

export type ComponentType = "schedule" | "events" | "shop" | "appointment";

/**
 * Theme tokens shared by every embed. Maps 1:1 to the CSS custom properties
 * applied on the component's shadow root host (see `theme/theme.ts`).
 * Names follow the handoff `*_TWEAK_DEFAULTS` keys.
 */
export interface ThemeConfig {
  accent: string;
  /** Pressed/hover state for the accent. Defaults to a darkened accent. */
  accentDeep?: string | undefined;
  /** Soft accent wash (e.g. selected day background). */
  accentSoft?: string | undefined;
  background: string;
  /** Panel / surface color (calendar + filter chrome). */
  panel: string;
  lineColor: string;
  /** Primary heading text color. */
  headline: string;
  /** Secondary / detail text color. */
  details: string;
  panelInk: string;
  panelInkSoft: string;
  font: string;
  weight: number;
  /** Corner radius in px. */
  radius: number;
  buttonTextColor: string;
}

/** Corner style applied to buttons/pills/switchers via `--zui-btn-radius`. */
export type ButtonShape = "square" | "rounded" | "pill";

/** Common toggles/labels available to all components. */
export interface BaseComponentOptions {
  /** Override the CTA label (e.g. "Book class", "Register"). */
  buttonText?: string | undefined;
  /**
   * Corner shape for every button/pill/switcher. Maps to the
   * `--zui-btn-radius` token (square = 0, rounded = 8px, pill = 999px).
   * Falls back to the theme radius token when unset.
   */
  buttonShape?: ButtonShape | undefined;
  hideTitle?: boolean | undefined;
  hideSignIn?: boolean | undefined;
}

export interface ScheduleOptions extends BaseComponentOptions {
  /** Page heading (defaults to "Book a Class"). */
  headerTitle?: string | undefined;
  /** Default view on load. 'month' is surfaced to visitors as "Calendar". */
  defaultView?: "list" | "day" | "list-week" | "month" | "week" | undefined;
  /** Surface the Calendar | Day | Week list | Week | List switcher to the visitor. */
  viewSwitcherEnabled?: boolean | undefined;
  rowStyle?: "lines" | "cards" | undefined;
  thumbnail?: "instructor" | "class" | "none" | undefined;
  capacityMode?: "all" | "low-only" | "none" | undefined;
  capacityThreshold?: number | undefined;
  /** 'YYYY-MM-DD'; empty = today. */
  startDate?: string | undefined;
  hideFilters?: boolean | undefined;
  /** Hide just the location filter pill (e.g. single-location studios). */
  hideLocationFilter?: boolean | undefined;
  hideDateStrip?: boolean | undefined;
  hideDuration?: boolean | undefined;
  hideThumbnail?: boolean | undefined;
  hideInstructor?: boolean | undefined;
  hideLocation?: boolean | undefined;
  hideCapacity?: boolean | undefined;
  hideCancelled?: boolean | undefined;
  hideListView?: boolean | undefined;
  /** Hide the calendar (month-grid) view from the switcher. */
  hideCalendarView?: boolean | undefined;
  /** Hide the single-day list view from the switcher. */
  hideDayView?: boolean | undefined;
  /** Hide the seven-day list while retaining the Week columns. */
  hideWeekListView?: boolean | undefined;
  /** Hide the week (7-day column) view from the switcher. */
  hideWeekView?: boolean | undefined;
  /** Pre-filter the schedule to only these class/activity ids (ST1). Empty =
   * all. Set in embed settings; the visitor sees only the matching items. */
  filterClassIds?: string[] | undefined;
  /** Pre-filter the schedule to only these instructor names (ST1). */
  filterInstructors?: string[] | undefined;
  /** Pre-filter the schedule to only these location names (ST1). */
  filterLocations?: string[] | undefined;
  /** ST3: require a phone number in the "your details" step before checkout. */
  requirePhone?: boolean | undefined;
  /** Hide the "I'm booking this for someone else" (family/friend) panel. */
  hideRecipientBooking?: boolean | undefined;
  /** Include standalone events alongside classes and programs; defaults to true. Explicit false hides one-offs. */
  includeOneOffEvents?: boolean | undefined;
}

export interface EventsOptions extends BaseComponentOptions {
  /** Host handoff: select this exact resource from the loaded allowed catalog. */
  initialEventId?: string | undefined;
  /** Server-issued participant key for the existing commerce checkout. */
  initialTargetKey?: string | undefined;
  thumbnailRatio?: "3:2" | "16:9" | "1:1" | undefined;
  hideFilters?: boolean | undefined;
  hideThumbnail?: boolean | undefined;
  hideDateTag?: boolean | undefined;
  hideDescription?: boolean | undefined;
  hideLocation?: boolean | undefined;
  hidePrice?: boolean | undefined;
  hideRegister?: boolean | undefined;
  /** ST1 embed pre-filter — only show these event ids (empty = all). */
  filterEventIds?: string[] | undefined;
  /** ST1 embed pre-filter — only these hosts/instructors (empty = all). */
  filterInstructors?: string[] | undefined;
  /** ST1 embed pre-filter — only these locations (empty = all). */
  filterLocations?: string[] | undefined;
  /** ST3: require a phone number in the "your details" step before checkout. */
  requirePhone?: boolean | undefined;
}

export interface ShopOptions extends BaseComponentOptions {
  /** Host-only deep link: open this catalog/Stripe product immediately. */
  initialProductId?: string | undefined;
  callout?: string | undefined;
  calloutText?: string | undefined;
  hideCallouts?: boolean | undefined;
  hideDescription?: boolean | undefined;
  hideButton?: boolean | undefined;
  /** Purchase-checkout toggles (passed through to the checkout step). */
  hideTerms?: boolean | undefined;
  hideBillingAddress?: boolean | undefined;
  /** ST3: require a phone number in the "your details" step before checkout. */
  requirePhone?: boolean | undefined;
  /** Hide the "purchasing for someone else" (family/friend) panel. */
  hideRecipientBooking?: boolean | undefined;
}

export interface AppointmentOptions extends BaseComponentOptions {
  /** Restrict to a single service category, or hide the category headers. */
  category?: string | undefined;
  hidePrices?: boolean | undefined;
  hideThumbnail?: boolean | undefined;
  /** ST1 embed pre-filter — only these service/appointment-type ids (empty = all). */
  filterServiceIds?: string[] | undefined;
  /** ST1 embed pre-filter — only these providers/instructors (empty = all). */
  filterInstructors?: string[] | undefined;
  /** ST1 embed pre-filter — only these locations (empty = all). */
  filterLocations?: string[] | undefined;
  /** ST3: require a phone number in the "your details" step before checkout. */
  requirePhone?: boolean | undefined;
  /** A5: hide the "booking for someone else" (family/friend) panel in appt checkout. */
  hideRecipientBooking?: boolean | undefined;
}

export type ComponentOptions =
  | ScheduleOptions
  | EventsOptions
  | ShopOptions
  | AppointmentOptions;

/**
 * Profile fields the ORG requires before someone can become a member
 * (`Org.attributes.requiredSignupFields`, PLAT-4456).
 *
 * Distinct from `options.requirePhone`, which is embed configuration the
 * embedding site owns and can turn off. These come from the org and are
 * re-checked by the API, so treating them as advisory only produces a rejected
 * checkout — collect them, don't hide them.
 */
export interface RequiredSignupFields {
  minimumAge?: number | undefined;
  ageTimeZone?: string | undefined;
  dateOfBirth?: boolean | undefined;
  /** Requires BOTH a contact name and a contact phone. */
  emergencyContact?: boolean | undefined;
  /** The member's OWN phone. ORs with `options.requirePhone`. */
  phoneNumber?: boolean | undefined;
}

/** Public Stripe context for checkout (Stripe Connect). Absent until the org connects Stripe. */
export interface EmbedStripe {
  publishableKey: string;
  accountId: string;
  /** Org currency, lowercase ISO 4217 (e.g. `nzd`). Absent from older APIs. */
  currency?: string | undefined;
  /** Connected account country for wallets (e.g. `NZ`). */
  country?: string | undefined;
}

/** The full payload returned for a single instance id. */
export interface EmbedConfig<O extends ComponentOptions = ComponentOptions> {
  instanceId: string;
  componentType: ComponentType;
  /** Org/location scoping the data source the component reads. */
  orgId: number;
  orgSlug: string;
  locationId?: string | null | undefined;
  theme: ThemeConfig;
  options: O;
  /**
   * Whether the org sells gift cards (`EmbedConfigDto.giftCardsEnabled`).
   *
   * OPT-OUT — only an explicit `false` disables. Optional here on purpose: the
   * older public config payloads may be cached and
   * simply omit the field, so `undefined` must keep meaning ENABLED. Always
   * test it as `!== false`, never `=== true` / `Boolean(...)`; that inversion
   * is the `issuePunchCard` bug that silently suppressed embed credits.
   *
   * Mirrors the server guard in `gift-card.service.ts`, which rejects
   * `/gift-cards/purchase-intent` when `enableGiftCards === false`.
   */
  giftCardsEnabled?: boolean | undefined;
  /**
   * Org-required signup fields (`EmbedConfigDto.requiredSignupFields`).
   *
   * OPT-IN, unlike `giftCardsEnabled`: the public config response is
   * older payloads may omit it, and
   * `undefined` has to keep meaning "nothing extra is required". Read it
   * through `resolveRequiredSignupFields()` rather than destructuring, so the
   * missing case stays in one place.
   */
  requiredSignupFields?: RequiredSignupFields | undefined;
  /**
   * Org ISO-4217 currency (`EmbedConfigDto.currency`). Optional because older
   * edge-cached payloads omit it; the money formatters treat missing as USD.
   */
  currency?: string | undefined;
  /** Present only when the org has a connected Stripe account. */
  stripe?: EmbedStripe | undefined;
}

export const DEFAULT_THEME: ThemeConfig = {
  accent: "#4F9DB5",
  // accentDeep / accentSoft intentionally omitted: they are DERIVED from the
  // chosen `accent` in theme/theme.ts when a config does not set them
  // explicitly, so a custom accent no longer falls back to the old fixed teal.
  background: "#FFFFFF",
  panel: "#F9F9F9",
  lineColor: "#ECECEC",
  headline: "#1F2A22",
  details: "#516056",
  panelInk: "#1F2A22",
  panelInkSoft: "#516056",
  font: "'Poppins', system-ui, sans-serif",
  weight: 500,
  radius: 8,
  buttonTextColor: "#FFFFFF",
};
